import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, ErrorState, Icon, Skeleton } from "@sagot/ui";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext, useWorkspaceId } from "../../../session/workspace-context";
import { count, num } from "../../service/reports/report-figures";
import { addDays, todayIn } from "../../service/reports/report-range";
import { useReport } from "../../service/reports/use-report";
import { serviceKeys } from "../../service/service-keys";
import { useServiceAreaAccess } from "../../service/use-service-area";
import { useServiceQuery } from "../../service/use-service-query";
import { useFormatters } from "../shared/format";

const CSAT_DAYS = 30;
const COUNTS_STALE_MS = 30_000;
const OPEN_VIEW = "all_open";
const BREACHING_VIEW = "breaching_soon";

function Stat({ label, value, hint, href, testId }: { label: string; value: string; hint?: string; href?: string; testId: string }) {
  return (
    <div data-testid={testId}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{href ? <AppLink href={href} className="hover:underline focus-visible:outline-2 focus-visible:outline-primary">{value}</AppLink> : value}</div>
      {hint ? <div className="mt-1 text-xs text-fg-subtle">{hint}</div> : null}
    </div>
  );
}

/**
 * The Service Desk card of the dashboard (RPT-002): open tickets, tickets breaching their SLA within the hour, and CSAT over the
 * last 30 days. Shown only where the workspace has the Service Desk and the member may read tickets; CSAT only with the reports
 * permission. Everything comes from the ticket view counts and the CSAT report, never a scan of the tickets.
 */
export function ServiceDashboardCard() {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const ws = useWorkspaceId();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const access = useServiceAreaAccess("tickets");
  const visible = access.enabled && !access.locked && permissions.can("tickets.view", { anywhere: true });
  const showCsat = permissions.can("tickets.reports", { anywhere: true });
  const counts = useServiceQuery({
    queryKey: [...serviceKeys.tickets(ws), "dashboard-counts"],
    enabled: visible,
    staleTime: COUNTS_STALE_MS,
    request: (api, signal) => api.GET("/v1/ticket-views/counts", { signal }),
  });
  const today = todayIn(fmt.timeZone);
  const csat = useReport("csat", { from: addDays(today, 1 - CSAT_DAYS), to: today, compare: false }, { enabled: visible && showCsat });
  if (!visible) return null;
  const loading = counts.isPending || (showCsat && csat.isPending);
  const failed = counts.isError || (showCsat && csat.isError);
  const responses = count(csat.data?.totals, "responses");
  const score = num(csat.data?.totals, "mean_score");
  return (
    <section className="card" aria-labelledby="service-card-title" data-testid="service-dashboard-card">
      <div className="card-header">
        <div>
          <h2 id="service-card-title" className="card-title">
            {t("service.cards.dashboard.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("service.cards.dashboard.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Button asChild variant="ghost" size="sm">
            <AppLink href={consoleRoutes.tickets(summary.slug)}>
              <Icon name="ticket" />
              {t("service.cards.dashboard.openTickets")}
            </AppLink>
          </Button>
          {showCsat ? (
            <Button asChild variant="ghost" size="sm">
              <AppLink href={consoleRoutes.serviceReports(summary.slug)}>
                <Icon name="chart-column" />
                {t("service.cards.dashboard.openReports")}
              </AppLink>
            </Button>
          ) : null}
        </div>
      </div>
      {loading ? (
        <div className="card-body grid grid-cols-1 gap-4 sm:grid-cols-3" aria-busy="true">
          {[0, 1, 2].map((tile) => (
            <div key={tile}>
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-3 h-8 w-16" />
              <Skeleton className="mt-3 h-3 w-32" />
            </div>
          ))}
        </div>
      ) : failed ? (
        <ErrorState
          headingLevel={3}
          title={t("service.cards.dashboard.loadError")}
          retryLabel={t("service.cards.dashboard.retry")}
          onRetry={() => {
            void counts.refetch();
            if (showCsat) void csat.refetch();
          }}
        />
      ) : (
        <div className="card-body grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat testId="service-open" label={t("service.cards.dashboard.open")} value={fmt.number(counts.data.counts[OPEN_VIEW] ?? 0)} hint={t("service.cards.dashboard.openHint")} href={consoleRoutes.tickets(summary.slug)} />
          <Stat
            testId="service-breaching"
            label={t("service.cards.dashboard.breaching")}
            value={fmt.number(counts.data.counts[BREACHING_VIEW] ?? 0)}
            hint={t("service.cards.dashboard.breachingHint")}
            href={`${consoleRoutes.tickets(summary.slug)}?view=${BREACHING_VIEW}`}
          />
          {showCsat ? (
            <Stat
              testId="service-csat"
              label={t("service.cards.dashboard.csat")}
              value={score === null || responses === 0 ? t("service.reports.noValue") : new Intl.NumberFormat(fmt.locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(score)}
              hint={responses === 0 ? t("service.cards.dashboard.csatNone") : t("service.cards.dashboard.csatHint", { count: responses })}
            />
          ) : null}
        </div>
      )}
    </section>
  );
}
